/**
 * @file index.tsx
 *
 * @fileoverview The HeroRow component.
 */

import { cva, VariantProps } from "class-variance-authority";
import React from "react";
import {
  generateMarginClasses,
  generatePaddingClasses,
  SpaceProps,
} from "../../../../theme/spacing";
import Box from "../../../02-atoms/07-box";
import styles from "./heroRow.module.scss";

const heroWrapper = cva([styles.heroWrapper], {
  variants: {
    boxPosition: {
      left: styles.heroWrapperBoxPositionLeft,
      bottom: styles.heroWrapperBoxPositionBottom,
      background: null,
    },
  },
});
const heroOverlay = cva([styles.heroOverlay], {
  variants: {
    boxPositionLeft: {
      true: styles.heroOverlayBoxPositionLeft,
    },
    imageBackground: {
      false: styles.heroOverlayImageTransparentBackground,
    },
  },
});
const heroText = cva([styles.heroText], {
  variants: {
    boxPositionLeft: {
      true: styles.heroTextBoxPositionLeft,
    },
  },
});
const boxContentCva = cva([styles.heroBoxContent], {
  variants: {
    boxPositionLeft: {
      true: styles.heroBoxContentBoxPositionLeft,
    },
  },
});
const heroPicture = cva([styles.heroPicture], {
  variants: {
    setBackground: {
      true: styles.heroPictureSetBackground,
    },
    boxPosition: {
      left: styles.heroPictureBoxPositionLeft,
      background: styles.heroPictureBoxPositionBackground,
      bottom: null,
    },
  },
});

export interface HeroRowProps extends SpaceProps, VariantProps<typeof heroWrapper> {
  /**
   * The image to render as background.
   */
  image: React.ReactNode;
  /**
   * The content to display on top of the image.
   */
  children?: React.ReactNode;
  /**
   * Content to show inside a box on the bottom part of the banner.
   */
  boxContent?: React.ReactNode;
  /**
   * Should the image background be set.
   */
  imageBackground?: boolean;
}

/**
 * This component provides a hero image and an overlaying text.
 */
export const HeroRow = ({
  image,
  children,
  boxContent,
  boxPosition,
  imageBackground = true,
  ...props
}: HeroRowProps) => {
  const boxPositionLeft = boxPosition === "left";

  return (
    <div className={heroWrapper({ boxPosition })}>
      <div className={heroOverlay({ boxPositionLeft, imageBackground })}>
        <div
          className={heroPicture({
            setBackground: !Boolean(image),
            boxPosition,
          })}
        >
          {image}
        </div>
      </div>
      {children && (
        <div
          className={`
          ${heroText({ boxPositionLeft })}
          ${generateMarginClasses(props, styles)}
          ${generatePaddingClasses(props, styles)}`}
        >
          {children}
        </div>
      )}
      {boxContent && (
        <Box className={boxContentCva({ boxPositionLeft })}>{boxContent}</Box>
      )}
    </div>
  );
};

export default HeroRow;
